import { configureStore } from '@reduxjs/toolkit'
import { api } from '@proaxiv/server-api'
import eventsReducer, { DEFAULT_BRANDING } from './store/eventsSlice'
import invitationsReducer from './store/invitationsSlice'
import registrationsReducer from './store/registrationsSlice'
import type { AppEvent } from './store/eventsSlice'
import type { Invitation } from './store/invitationsSlice'
import type { Registration } from './store/registrationsSlice'

const LS_EVENTS = 'proaxiv.events'
const LS_INVITATIONS = 'proaxiv.invitations'
const LS_REGISTRATIONS = 'proaxiv.registrations'

/**
 * Dev-only sample events. Loaded when localStorage is empty so that previews
 * (Responsively App device frames, fresh browsers, etc.) always show realistic
 * data without manual entry. Never used in production builds.
 */
const SEED_EVENTS: AppEvent[] = [
  {
    id: 'ev-seed-1',
    title: 'Tech Summit 2026',
    status: 'published',
    address: 'Tokyo International Forum, Tokyo, Japan',
    startDateTime: new Date('2026-09-15T09:00:00').toISOString(),
    endDateTime: new Date('2026-09-15T17:00:00').toISOString(),
    text: 'Annual technology conference bringing together industry leaders.',
    ticketActivation: new Date('2026-09-01T00:00:00').toISOString(),
    branding: DEFAULT_BRANDING,
    createdAt: new Date('2026-06-01T00:00:00').toISOString(),
  },
  {
    id: 'ev-seed-2',
    title: 'Summer Music Festival',
    status: 'draft',
    address: 'Yoyogi Park, Shibuya, Tokyo',
    startDateTime: new Date('2026-08-20T16:00:00').toISOString(),
    text: 'Open-air music festival with local and international artists.',
    ticketActivation: new Date('2026-08-01T00:00:00').toISOString(),
    branding: DEFAULT_BRANDING,
    createdAt: new Date('2026-06-10T00:00:00').toISOString(),
  },
]

function loadEvents(): AppEvent[] {
  try {
    const raw = localStorage.getItem(LS_EVENTS)
    if (raw) return JSON.parse(raw) as AppEvent[]
    // No saved events yet — seed sample data in dev so previews aren't empty.
    return import.meta.env.DEV && import.meta.env.MODE !== 'test' ? SEED_EVENTS : []
  } catch {
    return import.meta.env.DEV && import.meta.env.MODE !== 'test' ? SEED_EVENTS : []
  }
}

function loadInvitations(): Invitation[] {
  try {
    const raw = localStorage.getItem(LS_INVITATIONS)
    return raw ? (JSON.parse(raw) as Invitation[]) : []
  } catch {
    return []
  }
}

function loadRegistrations(): Registration[] {
  try {
    const raw = localStorage.getItem(LS_REGISTRATIONS)
    return raw ? (JSON.parse(raw) as Registration[]) : []
  } catch {
    return []
  }
}

export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    events: eventsReducer,
    invitations: invitationsReducer,
    registrations: registrationsReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(api.middleware),
  preloadedState: {
    events: { list: loadEvents() },
    invitations: { list: loadInvitations() },
    registrations: { list: loadRegistrations() },
  },
})

// Debounced, change-gated localStorage persistence for events, invitations, and registrations.
// Avoids serializing on every keypress / RTK Query action.
let saveTimer: ReturnType<typeof setTimeout> | null = null
let lastSavedEvents = store.getState().events.list
let lastSavedInvitations = store.getState().invitations.list
let lastSavedRegistrations = store.getState().registrations.list

store.subscribe(() => {
  const state = store.getState()
  const eventsChanged = state.events.list !== lastSavedEvents
  const invitationsChanged = state.invitations.list !== lastSavedInvitations
  const registrationsChanged = state.registrations.list !== lastSavedRegistrations

  if (!eventsChanged && !invitationsChanged && !registrationsChanged) return

  lastSavedEvents = state.events.list
  lastSavedInvitations = state.invitations.list
  lastSavedRegistrations = state.registrations.list

  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    try {
      if (eventsChanged) localStorage.setItem(LS_EVENTS, JSON.stringify(lastSavedEvents))
      if (invitationsChanged)
        localStorage.setItem(LS_INVITATIONS, JSON.stringify(lastSavedInvitations))
      if (registrationsChanged)
        localStorage.setItem(LS_REGISTRATIONS, JSON.stringify(lastSavedRegistrations))
    } catch {
      // storage quota exceeded — silently ignore
    }
    saveTimer = null
  }, 500)
})

/** Inferred root state type from the Redux store. */
export type RootState = ReturnType<typeof store.getState>
/** Inferred dispatch type from the Redux store. */
export type AppDispatch = typeof store.dispatch
